import type { $AxisDirection } from '@kitware/vtk.js';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import type { Vector3 } from '@kitware/vtk.js/types';
import type { FrameDataFragment, ImageParams } from 'generated/graphql';
import type { DreAllViewportTypes } from 'config/constants';
import type {
  FullSingleLayerStack,
  CompositeFrame,
  CompositeMatchType,
  CompositeStackConfig,
  Series,
  SeriesMatch,
  Stack,
  Study,
} from '../../../ViewportsConfigurations/types';
import type {
  BaseImagingProviderArgs,
  EmitterEvent,
  ProviderStatus,
  SlicePlane,
} from './BaseImagingProvider';
import {
  BaseImagingProvider,
  emitterEvents,
  createActiveSliceChangedEmitterEvent,
} from './BaseImagingProvider';
import { BaseSingleStackImagingProvider } from './BaseSingleStackImagingProvider';
import type { Handler } from 'mitt';
import type { SupportedTextureTypes } from 'utils/textureUtils';
import type { RenderManager } from './RenderManager';

type ConstituentListenerRecord = {
  provider: BaseSingleStackImagingProvider;
  type: EmitterEvent;
  handler: Handler<unknown>;
};

/**
 * Arguments for creating a MultiObjectImagingProvider.
 * Requires the top provider and matches from the matching utility.
 */
export type MultiObjectImagingProviderArgs = Omit<
  BaseImagingProviderArgs<FullSingleLayerStack>,
  'stack'
> & {
  /** Synthetic composite stack SMID used as this provider's identity */
  compositeStackSmid: string;
  /** Configuration containing top stack and matched series */
  config: CompositeStackConfig;
  /** The top stack's existing imaging provider */
  topProvider: BaseSingleStackImagingProvider;
};

/**
 * Imaging provider for composite stacks.
 *
 * Concatenates frames from a top stack and one or more matched stacks
 * into a single scrollable sequence. Reuses existing imaging providers for pixel
 * data delegation (no new WorkerLoaded providers created).
 *
 * Key characteristics:
 * - Always non-3Dable (matched stacks may have different orientations/spacing)
 * - Frames ordered: top first, then matched by date (oldest first)
 * - Delegates getImage/getFramePixels to constituent providers
 * - Tracks study origin per frame for UI indicators
 */
export class MultiObjectImagingProvider extends BaseImagingProvider<FullSingleLayerStack> {
  /** Constituent providers: top first, then matched in date order */
  #constituentProviders: BaseSingleStackImagingProvider[] = [];

  /** Concatenated frame list with study metadata */
  #concatenatedFrames: CompositeFrame[] = [];

  /** Map from frame SMID to the provider that owns it */
  #frameToProviderMap: Map<string, BaseSingleStackImagingProvider> = new Map();

  /** Map from concatenated index to frame SMID */
  #frameSmidsMap: { [key: number]: string } = {};

  /** The composite configuration */
  #config: CompositeStackConfig;

  /** Cached frame tags (rebuilt in #buildConcatenatedFrameList) */
  #cachedFrameTags: ReadonlyArray<FrameDataFragment> = [];

  /** Cached unique series SMIDs (rebuilt in #buildConcatenatedFrameList) */
  #cachedSeriesSmids: string[] = [];

  /** Cached first concatenated index for each matched study SMID (rebuilt in #buildConcatenatedFrameList) */
  #firstFrameIndexByStudySmid: Map<string, number> = new Map();

  /** Render manager for triggering re-renders after sync */
  #renderManager: RenderManager;

  /** Index of the currently active provider (updated on scroll) */
  #activeProviderIndex: number = 0;

  /** Reference to all stack providers for cross-object syncing */
  #allStackProviders: Map<string, BaseImagingProvider<Stack>> | null = null;

  /** Track the last study we were viewing for detecting object boundary crossings */
  #lastViewedStudySmid: string;

  /** Handler records for listeners attached to constituent providers' emitters */
  #constituentListeners: ConstituentListenerRecord[] = [];

  get #activeProvider(): BaseSingleStackImagingProvider {
    return this.#constituentProviders[this.#activeProviderIndex] ?? this.#constituentProviders[0];
  }

  /** Synthetic SMID used as this provider's identity in the provider map */
  readonly compositeSmid: string;

  constructor(args: MultiObjectImagingProviderArgs) {
    const { config, topProvider, compositeStackSmid, ...baseArgs } = args;

    // Pass the real topStack to super — no SMID blending.
    // The compositeSmid field holds the synthetic identity for map lookups.
    super({
      ...baseArgs,
      stack: config.topStack,
    });

    this.compositeSmid = compositeStackSmid;
    this.linkSmid = compositeStackSmid;
    this.#config = config;
    this.#renderManager = baseArgs.renderManager;
    this.#lastViewedStudySmid = config.topStudy.smid;
    this.lpsDirections = topProvider.lpsDirections;
    this.type = 'composite';

    // Build the concatenated frame list from the primary stack
    this.#constituentProviders = [topProvider];
    this.#buildConcatenatedFrameList();
  }

  /**
   * Wire up references to matched stack providers.
   * Called after provider map is populated with all stack providers.
   */
  calculateConstituentProviders(stackProviders: Map<string, BaseImagingProvider<Stack>>): void {
    this.#constituentProviders = this.#buildConstituentProviders(stackProviders);

    // Rebuild concatenated frame list now that we have all providers
    this.#buildConcatenatedFrameList();

    // Update status based on constituent providers
    this.setStatus(this.#getComputedStatus());
  }

  /**
   * Build the full constituent providers array (topProvider + matched providers)
   * and wire up event listeners for all of them.
   */
  #buildConstituentProviders(
    stackProviders: Map<string, BaseImagingProvider<Stack>>
  ): BaseSingleStackImagingProvider[] {
    const topProvider = this.#constituentProviders[0];
    const providers: BaseSingleStackImagingProvider[] = [topProvider];

    // Collect all providers: top + matched
    for (const seriesMatch of this.#config.matches) {
      const matchedProvider = stackProviders.get(seriesMatch.stack.smid);

      if (!matchedProvider || !(matchedProvider instanceof BaseSingleStackImagingProvider)) {
        continue;
      }

      providers.push(matchedProvider);
    }

    // Clear any previously attached listeners before re-subscribing. This guards against
    // repeated calls accumulating handlers on constituent emitters.
    for (const { provider, type, handler } of this.#constituentListeners) {
      provider.emitter.off(type, handler);
    }
    this.#constituentListeners = [];

    // Wire event listeners for all constituent providers (including top)
    for (const provider of providers) {
      const onFrameLoaded: Handler<unknown> = () => {
        this.emitter.emit(emitterEvents.frameLoaded, this.getFramesLoaded());
        this.viewRef.current?.requestRender();
      };
      provider.emitter.on(emitterEvents.frameLoaded, onFrameLoaded);
      this.#constituentListeners.push({
        provider,
        type: emitterEvents.frameLoaded,
        handler: onFrameLoaded,
      });

      const onStackLoaded: Handler<unknown> = () => {
        if (providers.every((p) => p.status === 'complete')) {
          this.emitter.emit(emitterEvents.stackLoaded);
        }
      };
      provider.emitter.on(emitterEvents.stackLoaded, onStackLoaded);
      this.#constituentListeners.push({
        provider,
        type: emitterEvents.stackLoaded,
        handler: onStackLoaded,
      });

      const onStatusUpdated: Handler<unknown> = () => {
        this.setStatus(this.#getComputedStatus());
      };
      provider.emitter.on(emitterEvents.statusUpdated, onStatusUpdated);
      this.#constituentListeners.push({
        provider,
        type: emitterEvents.statusUpdated,
        handler: onStatusUpdated,
      });

      const onVolumeUpdated: Handler<unknown> = () => {
        this.emitter.emit(emitterEvents.volumeUpdated);
      };
      provider.emitter.on(emitterEvents.volumeUpdated, onVolumeUpdated);
      this.#constituentListeners.push({
        provider,
        type: emitterEvents.volumeUpdated,
        handler: onVolumeUpdated,
      });
    }

    return providers;
  }

  /**
   * Resolve the constituent provider and local frame index for a given concatenated index.
   * Centralizes the repeated pattern of mapping concatenatedIndex → {provider, providerFrameIndex}.
   */
  #resolveProviderForSlice(
    concatenatedIndex: number
  ): { provider: BaseSingleStackImagingProvider; providerFrameIndex: number } | null {
    const compositeFrame = this.#concatenatedFrames[concatenatedIndex];
    if (compositeFrame == null) return null;

    const provider = this.#frameToProviderMap.get(compositeFrame.frame.smid);
    if (provider == null) return null;

    return { provider, providerFrameIndex: compositeFrame.providerFrameIndex };
  }

  /**
   * Build the concatenated frame list from top + matched stacks.
   * Top frames come first, then matched sorted by date (oldest first).
   * Also rebuilds all derived caches (frameTags, seriesSmids, firstFrameIndex).
   */
  #buildConcatenatedFrameList(): void {
    this.#concatenatedFrames = [];
    this.#frameToProviderMap.clear();
    this.#frameSmidsMap = {};

    let concatenatedIndex = 0;

    // Add top stack frames first
    const topProvider = this.#constituentProviders[0];
    const topFrameOfReference = topProvider.getFrameOfReference();

    for (let frameIdx = 0; frameIdx < this.#config.topStack.frames.length; frameIdx++) {
      const frame = this.#config.topStack.frames[frameIdx];
      this.#concatenatedFrames.push({
        frame,
        concatenatedIndex,
        studySmid: this.#config.topStudy.smid,
        isTop: true,
        studyDate:
          'studyDate' in this.#config.topStudy
            ? (this.#config.topStudy.studyDate as string | null)
            : null,
        frameOfReferenceUID: topFrameOfReference ?? null,
        providerFrameIndex: frameIdx,
      });
      this.#frameToProviderMap.set(frame.smid, topProvider);
      this.#frameSmidsMap[concatenatedIndex] = frame.smid;
      concatenatedIndex++;
    }

    // Add matched stack frames (already sorted by date in matches)
    for (let i = 0; i < this.#config.matches.length; i++) {
      const seriesMatch = this.#config.matches[i];
      // Provider index is i+1 because top is at index 0
      const matchedProvider = this.#constituentProviders[i + 1];

      if (matchedProvider == null) {
        // Provider not yet wired up - this is expected during initial construction
        continue;
      }

      const matchedFrameOfReference = matchedProvider.getFrameOfReference();

      for (let frameIdx = 0; frameIdx < seriesMatch.stack.frames.length; frameIdx++) {
        const frame = seriesMatch.stack.frames[frameIdx];
        this.#concatenatedFrames.push({
          frame,
          concatenatedIndex,
          studySmid: seriesMatch.study.smid,
          isTop: false,
          studyDate: seriesMatch.studyDate,
          frameOfReferenceUID: matchedFrameOfReference ?? null,
          providerFrameIndex: frameIdx,
        });
        this.#frameToProviderMap.set(frame.smid, matchedProvider);
        this.#frameSmidsMap[concatenatedIndex] = frame.smid;
        concatenatedIndex++;
      }
    }

    // Rebuild derived caches
    this.#cachedFrameTags = this.#concatenatedFrames.map((f) => f.frame);
    this.#cachedSeriesSmids = Array.from(
      new Set(this.#cachedFrameTags.map((frame) => frame.series.smid))
    );
    this.#firstFrameIndexByStudySmid = new Map();
    for (const compositeFrame of this.#concatenatedFrames) {
      if (!this.#firstFrameIndexByStudySmid.has(compositeFrame.studySmid)) {
        this.#firstFrameIndexByStudySmid.set(
          compositeFrame.studySmid,
          compositeFrame.concatenatedIndex
        );
      }
    }
  }

  /**
   * Compute aggregate status from constituent providers.
   */
  #getComputedStatus(): ProviderStatus {
    const statuses = this.#constituentProviders.map((p) => p.status);

    if (statuses.includes('error')) {
      return 'error';
    } else if (statuses.some((s) => s === 'loading')) {
      return 'loading';
    } else if (statuses.every((s) => s === 'complete')) {
      return 'complete';
    }

    return 'init';
  }

  /**
   * Update the active provider based on the current concatenated frame index.
   * Returns true if the active provider changed.
   */
  #updateActiveProvider(concatenatedIndex: number): boolean {
    const frame = this.#concatenatedFrames[concatenatedIndex];
    if (frame == null) return false;
    const provider = this.#frameToProviderMap.get(frame.frame.smid);
    if (provider == null) return false;
    const newIndex = this.#constituentProviders.indexOf(provider);
    if (newIndex === -1) return false;
    if (newIndex !== this.#activeProviderIndex) {
      this.#activeProviderIndex = newIndex;
      this.lpsDirections = this.#activeProvider.lpsDirections;
      this.emitter.emit(emitterEvents.activeStudyChanged, this.#activeProvider.study);
      this.study = this.#activeProvider.study;
      return true;
    }
    return false;
  }

  /**
   * Override setupStack to do nothing.
   * The base class calls setupStack() in its constructor, but at that point
   * our private fields aren't initialized yet. We delegate to the primary
   * provider for vtkImage/lpsDirections instead of setting up our own.
   */
  setupStack(): void {
    // Empty - we use the top provider's setup via vtkImage getter
  }

  /** Map of concatenated frame index to frame SMID */
  get frameSmidsMap(): { [key: number]: string } {
    return this.#frameSmidsMap;
  }

  /** Total frames across all stacks */
  get stackSize(): number {
    return this.#concatenatedFrames.length;
  }

  /** Frame metadata for all concatenated frames */
  get frameTags(): ReadonlyArray<FrameDataFragment> {
    return this.#cachedFrameTags;
  }

  /** Return the series for the currently active frame's provider. */
  getSeries(): Nullable<Series> {
    return this.#activeProvider?.getSeries() ?? super.getSeries();
  }

  /** Return unique series SMIDs across all concatenated frames (not just the top stack). */
  getSeriesSmids(): string[] {
    return this.#cachedSeriesSmids;
  }

  /** Image params from top stack */
  get imageParams(): ImageParams {
    return this.#config.topStack.imageParams;
  }

  getFrameAxes(): Set<$AxisDirection> {
    // Delegate to active provider
    return this.#activeProvider?.getFrameAxes() ?? new Set();
  }

  hasPixels(): boolean {
    return this.#constituentProviders.some((p) => p.hasPixels());
  }

  canRender(renderEngine: string): boolean {
    // Can render if active provider can render
    return this.#activeProvider?.canRender(renderEngine) ?? false;
  }

  hasValidDirection(): boolean {
    return this.#activeProvider?.hasValidDirection() ?? false;
  }

  /** Always non-3Dable - matched stacks may have different orientations/spacing */
  is3Dable(): boolean {
    return false;
  }

  /** Get VTK image for the current active slice from the correct constituent provider */
  get vtkImage(): vtkImageData {
    // Look up the active slice's frame SMID and get the per-frame image
    // from the constituent provider that owns it. This ensures camera
    // calculations use the correct image geometry when scrolling across
    // providers with different orientations/spacing.
    const activeSlice = this.getActiveSlice('TWO_D_DRE');
    const frameSmid = this.#frameSmidsMap[activeSlice];
    if (frameSmid != null) {
      const image = this.getImage(frameSmid);
      if (image != null) return image;
    }
    return this.#activeProvider?.vtkImage ?? super.vtkImage;
  }

  /** Return frame metadata at concatenated index */
  getFrameTagsForViewIndex(
    viewType: DreAllViewportTypes,
    position: number
  ): Nullable<FrameDataFragment> {
    return this.#concatenatedFrames[position]?.frame ?? null;
  }

  /** Get VTK image for a specific frame (from correct constituent provider) */
  getImage(frameSmid?: string | null): vtkImageData | null {
    if (frameSmid == null) return null;

    const provider = this.#frameToProviderMap.get(frameSmid);
    if (provider != null) {
      return provider.getImage(frameSmid);
    }

    // Fallback: search all providers
    for (const p of this.#constituentProviders) {
      const image = p.getImage(frameSmid);
      if (image != null) return image;
    }

    return null;
  }

  /** Get pixel data for a specific frame (from correct constituent provider) */
  getFramePixels(frameSmid: string): SupportedTextureTypes | null {
    const provider = this.#frameToProviderMap.get(frameSmid);
    if (provider != null) {
      return provider.getFramePixels(frameSmid);
    }

    // Fallback: search all providers
    for (const p of this.#constituentProviders) {
      const pixels = p.getFramePixels(frameSmid);
      if (pixels != null) return pixels;
    }

    return null;
  }

  /** Get color transfer function for a frame */
  getColorTransferFunctionForFrame(frameSmid: Nullable<string>): vtkColorTransferFunction | null {
    if (frameSmid == null) return null;

    const provider = this.#frameToProviderMap.get(frameSmid);
    if (provider != null) {
      return provider.getColorTransferFunctionForFrame(frameSmid);
    }

    return null;
  }

  /** Estimate memory usage across all constituent providers */
  estimateMemory(): number {
    // Composite provider doesn't own frame memory; constituents account for their own usage.
    return 0;
  }

  estimatePerFrameMemory(): number {
    return 0;
  }

  /** Count loaded frames across all constituent providers */
  getFramesLoaded(): number {
    return this.#constituentProviders.reduce((sum, p) => sum + p.getFramesLoaded(), 0);
  }

  /** Check if a specific frame is loaded */
  isFrameLoaded(frameSmid: string): boolean {
    const provider = this.#frameToProviderMap.get(frameSmid);
    if (provider != null) {
      return provider.isFrameLoaded(frameSmid);
    }
    return this.#constituentProviders.some((p) => p.isFrameLoaded(frameSmid));
  }

  /** Ready to render if at least one frame is loaded */
  isReadyToRender(): boolean {
    return this.#constituentProviders.some((p) => p.isReadyToRender());
  }

  getVolume(): SupportedTextureTypes {
    // Non-3Dable, so this shouldn't be called, but delegate to primary
    return this.#constituentProviders[0]?.getVolume() ?? new Float32Array(0);
  }

  getRange(): [number, number] | null {
    return this.#activeProvider?.getRange() ?? null;
  }

  getRangeForSlice(slice: number): [number, number] | null {
    const resolved = this.#resolveProviderForSlice(slice);
    if (resolved == null) return null;

    return resolved.provider.getRangeForSlice(resolved.providerFrameIndex);
  }

  /** Get the slice plane at a concatenated index */
  getSlicePlaneAtViewIndex(
    viewType: DreAllViewportTypes,
    viewIndex: number,
    layerIndex: number = 0
  ): Nullable<SlicePlane> {
    const resolved = this.#resolveProviderForSlice(viewIndex);
    if (resolved == null) return null;

    return resolved.provider.getSlicePlaneAtViewIndex(
      viewType,
      resolved.providerFrameIndex,
      layerIndex
    );
  }

  /** Not implemented - composite doesn't load directly */
  _loadStack(): Promise<void> {
    // Composite provider doesn't load directly; it delegates to constituents
    // Loading is handled by ViewerContext for each constituent provider
    return Promise.resolve();
  }

  cancelLoad(): void {
    // NOOP - constituent providers are cancelled individually via clearProviderMap
  }

  dispose(): void {
    for (const { provider, type, handler } of this.#constituentListeners) {
      provider.emitter.off(type, handler);
    }
    this.#constituentListeners = [];
    this.#constituentProviders = [];
    this.#frameToProviderMap.clear();
    this.#allStackProviders = null;
    this.#concatenatedFrames.length = 0;
    this.#frameSmidsMap = {};
    this.#cachedFrameTags = [];
    this.#cachedSeriesSmids = [];
    this.#firstFrameIndexByStudySmid.clear();
    super.dispose();
  }

  /** Get the study SMID for a frame at the given concatenated index */
  getStudySmidForFrame(index: number): string | null {
    return this.#concatenatedFrames[index]?.studySmid ?? null;
  }

  /** Check if a frame at the given index is from the top study */
  isFrameFromTopStudy(index: number): boolean {
    return this.#concatenatedFrames[index]?.isTop ?? true;
  }

  /** Get the study date for a frame at the given concatenated index */
  getStudyDateForFrame(index: number): string | null {
    return this.#concatenatedFrames[index]?.studyDate ?? null;
  }

  /** Get the frame of reference UID for a frame at the given concatenated index */
  getFrameOfReferenceForFrame(index: number): string | null {
    return this.#concatenatedFrames[index]?.frameOfReferenceUID ?? null;
  }

  /** Get the composite frame metadata at the given concatenated index */
  getCompositeFrame(index: number): CompositeFrame | null {
    return this.#concatenatedFrames[index] ?? null;
  }

  /** Get all constituent providers */
  getConstituentProviders(): ReadonlyArray<BaseSingleStackImagingProvider> {
    return this.#constituentProviders;
  }

  /** Get the number of constituent stacks (top + matched) */
  getConstituentStackCount(): number {
    return this.#constituentProviders.length;
  }

  /** Get the configuration used to create this provider */
  getConfig(): CompositeStackConfig {
    return this.#config;
  }

  /** Check if this is a composite stack provider */
  isCompositeStack(): boolean {
    return true;
  }

  /** Returns true if this composite stack contains hybrid (within-exam) matches */
  isHybridStack(): boolean {
    return this.#config.matches.some((m) => m.matchType === 'hybrid');
  }

  /** The stacking type determines sync behavior between composite providers */
  get stackingType(): CompositeMatchType {
    return this.isHybridStack() ? 'hybrid' : 'prior';
  }

  /** Get the top study */
  getTopStudy(): Study {
    return this.#config.topStudy;
  }

  /** Get the study for the currently active constituent provider (changes on scroll) */
  getActiveStudy(): Study {
    return this.#activeProvider?.study ?? this.#config.topStudy;
  }

  /** Get the matches */
  getMatches(): ReadonlyArray<SeriesMatch> {
    return this.#config.matches;
  }

  /**
   * Get the first frame index of a study by its SMID.
   * Returns null if the study is not found in this composite stack.
   */
  getFirstFrameIndexForStudy(studySmid: string): number | null {
    return this.#firstFrameIndexByStudySmid.get(studySmid) ?? null;
  }

  getNumberOfColorChannels(frameIndex: number): number {
    const frame = this.#concatenatedFrames[frameIndex]?.frame;
    return frame?.colorChannels ?? 1;
  }

  /**
   * Mark a slice as viewed for analytics tracking.
   * Delegates to the appropriate constituent provider.
   */
  markSliceViewed(viewType: DreAllViewportTypes, slice: number): Set<string> {
    const resolved = this.#resolveProviderForSlice(slice);
    if (resolved == null) return new Set();

    return resolved.provider.markSliceViewed(viewType, resolved.providerFrameIndex);
  }

  /**
   * Find the closest slice for a given world position.
   * Delegates to the currently active constituent provider.
   */
  getSliceForWorldPosition(
    position: Nullable<Vector3>,
    viewType: DreAllViewportTypes,
    clamp: boolean = true
  ): Nullable<number> {
    return this.#activeProvider.getSliceForWorldPosition(position, viewType, clamp);
  }

  /**
   * NOOP - composite stacks do not participate in automatic linked scrolling.
   * Cross-object syncing is handled explicitly via #syncOtherCompositeStacks.
   */
  calculateLinkedStacks(stackProviders: Map<string, BaseImagingProvider<Stack>>): void {
    // Store reference to all providers for cross-object syncing
    this.#allStackProviders = stackProviders;
  }

  /**
   * Composite stacks opt out of localization line rendering.
   */
  calculateIntersection(): [null, null] {
    return [null, null];
  }

  /**
   * Override _updateSliceAndLinks to add composite specific linking.
   * When scrolling into a matched study, other composite stacks with that same match
   * should switch to show it (regardless of position matching).
   */
  _updateSliceAndLinks(
    viewType: DreAllViewportTypes,
    viewportId: string,
    slice: number,
    slicePosition?: Vector3 | null,
    manualScrollDistance?: Vector3 | null,
    updateLinked?: boolean
  ): void {
    // Update active provider based on current scroll position
    this.#updateActiveProvider(slice);

    // Get current frame info before base implementation
    const currentFrame = this.#concatenatedFrames[slice];
    const currentStudySmid = currentFrame?.studySmid ?? null;
    const previousStudySmid = this.#lastViewedStudySmid;
    const crossedStudyBoundary = previousStudySmid !== currentStudySmid;

    // Update last viewed study
    if (currentStudySmid != null) {
      this.#lastViewedStudySmid = currentStudySmid;
    }

    // Call base implementation (handles normal linking)
    super._updateSliceAndLinks(
      viewType,
      viewportId,
      slice,
      slicePosition,
      manualScrollDistance,
      // Disable normal linked scrolling for composite - we handle it ourselves
      false
    );

    // Now handle composite specific linking
    if (updateLinked === false || this.#allStackProviders == null || currentFrame == null) {
      return;
    }

    // When we cross into a different study, sync other composite stacks
    // Both smids must be non-null to ensure we have valid study context
    if (
      this.stackingType === 'prior' &&
      crossedStudyBoundary &&
      currentStudySmid != null &&
      previousStudySmid != null
    ) {
      this.#syncOtherCompositeStacks(currentStudySmid, viewType);
    }
  }

  _updateVtkSliceForAllViewports(viewType: DreAllViewportTypes, slice: number) {
    super._updateVtkSliceForAllViewports(viewType, slice);
    const syncedFrame = this.#concatenatedFrames[slice];
    if (syncedFrame?.studySmid != null) {
      this.#lastViewedStudySmid = syncedFrame.studySmid;
    }
  }

  /**
   * Sync other composite stacks to show the same study.
   * When we scroll into a matched study, other composite stacks with the same
   * stacking type that contain that match jump to it.
   */
  #syncOtherCompositeStacks(studySmid: string, viewType: DreAllViewportTypes): void {
    if (this.#allStackProviders == null) {
      return;
    }

    let syncedCount = 0;

    for (const [, targetProvider] of this.#allStackProviders) {
      if (targetProvider === this) continue;
      if (!(targetProvider instanceof MultiObjectImagingProvider)) continue;
      if (targetProvider.stackingType !== this.stackingType) continue;

      const targetSlice = targetProvider.getFirstFrameIndexForStudy(studySmid);
      if (targetSlice == null) continue;

      targetProvider.#updateActiveProvider(targetSlice);
      targetProvider._updateVtkSliceForAllViewports(viewType, targetSlice);

      this.#renderManager.queueUpdate(
        targetProvider,
        createActiveSliceChangedEmitterEvent(viewType)
      );
      syncedCount++;
    }

    if (syncedCount > 0) {
      this.#renderManager.throttledRender(viewType);
    }
  }

  seriesAreHomogenous(): boolean {
    // Composite stacks are never homogenous (they span multiple studies)
    return false;
  }

  isSopClass(sopClass: string): boolean {
    return this.#activeProvider?.isSopClass(sopClass) ?? false;
  }

  /** Get the frame of reference UID (from active provider) */
  getFrameOfReference(): Nullable<string> {
    return this.#activeProvider?.getFrameOfReference() ?? null;
  }

  getViewportDisplayConfigKey({
    viewType,
    viewportId,
  }: {
    viewType: DreAllViewportTypes;
    viewportId: string;
  }): { stackSmid: string; viewType: DreAllViewportTypes; viewportId: string } {
    return this.#activeProvider.getViewportDisplayConfigKey({ viewType, viewportId });
  }
}
